/** Default hospital palette — matches current live site blues. */
export const DEFAULT_THEME_ID = 'hospital_blue';

export const DEFAULT_COLORS = {
  announcementBar: '#003d82',
  announcementBarEnd: '#0056b3',
  brandPrimary: '#003d82',
  brandPrimaryLight: '#0056b3',
  footerBg: '#2c3e50',
  pageHeader: '#003d82',
  surfaceTint: '#eef4fa',
  activeTheme: DEFAULT_THEME_ID,
  popupMessage: '',
  popupEnabled: false,
  festiveEffect: 'none'
};

/** Animation options admins can attach to a custom theme. */
export const FESTIVE_EFFECT_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'christmas', label: 'Christmas (snow, Santa, fireworks)' },
  { id: 'newyear', label: "New Year (snow & fireworks)" },
  { id: 'ghana', label: 'Ghana flag (national holidays)' },
  { id: 'independence', label: 'Independence Day (forest green · arch + flag rain)' },
  { id: 'constitution', label: 'Constitution Day (gold + coat of arms)' },
  { id: 'eid', label: 'Eid accents' },
  { id: 'ramadan', label: 'Ramadan (navy · lanterns, moon & stars)' },
  { id: 'mothers', label: "Mother's Day hearts" },
  { id: 'fathers', label: "Father's Day accents" },
  { id: 'valentine', label: "Valentine's hearts" },
  { id: 'easter', label: 'Easter petals' },
  { id: 'farmers', label: "Farmers' Day accents" },
  { id: 'africa', label: 'Africa / AU Day accents' },
  { id: 'boxing', label: 'Boxing Day sparkles' }
];

/**
 * Full-site event themes (admin one-click).
 * Colours follow Ghana national / cultural day guidance where listed.
 * `effect`: animation layer used by FestiveEffects
 * `defaultPopup`: suggested greeting when the theme is selected
 */
export const THEME_PRESETS = [
  {
    id: 'hospital_blue',
    name: 'Hospital Blue (default)',
    description: 'Everyday TGH branding',
    effect: 'none',
    defaultPopup: '',
    announcementBar: '#003d82',
    announcementBarEnd: '#0056b3',
    brandPrimary: '#003d82',
    brandPrimaryLight: '#0056b3',
    footerBg: '#2c3e50',
    pageHeader: '#003d82',
    surfaceTint: '#eef4fa'
  },
  {
    id: 'new_year',
    name: "New Year's / New Year's Eve",
    description: '31 Dec – 1 Jan · Christmas colours · snow & fireworks',
    effect: 'newyear',
    defaultPopup:
      'Happy New Year from Tema General Hospital! Wishing you good health throughout the year.',
    announcementBar: '#7a0c16',
    announcementBarEnd: '#c41e3a',
    brandPrimary: '#8b0000',
    brandPrimaryLight: '#c41e3a',
    footerBg: '#4a0a10',
    pageHeader: '#8b0000',
    surfaceTint: '#fdf2f3'
  },
  {
    id: 'constitution_day',
    name: 'Constitution Day',
    description: '7 January · Gold & yellow · Ghana coat of arms',
    effect: 'constitution',
    defaultPopup:
      'Happy Constitution Day! Tema General Hospital commemorates the adoption of Ghana’s 1992 Constitution.',
    announcementBar: '#b8860b',
    announcementBarEnd: '#fcd116',
    brandPrimary: '#c9a227',
    brandPrimaryLight: '#ffe566',
    footerBg: '#5c4800',
    pageHeader: '#b8860b',
    surfaceTint: '#fffbeb'
  },
  {
    id: 'valentine_chocolate',
    name: "Valentine's / National Chocolate Day",
    description: '14 February · Red & White · heart rain & love fireworks',
    effect: 'valentine',
    defaultPopup:
      "Happy Valentine’s Day and National Chocolate Day from Tema General Hospital! Celebrating love and Ghana’s cocoa.",
    announcementBar: '#b71c1c',
    announcementBarEnd: '#ffffff',
    brandPrimary: '#c62828',
    brandPrimaryLight: '#e53935',
    footerBg: '#7f0000',
    pageHeader: '#c62828',
    surfaceTint: '#fff5f5'
  },
  {
    id: 'independence_gold',
    name: 'Independence Day',
    description: '6 March · Forest green · Black Star Gate · raining Ghana flags',
    effect: 'independence',
    defaultPopup:
      'Happy Independence Day! Tema General Hospital commemorates Ghana’s independence from British rule in 1957.',
    announcementBar: '#063820',
    announcementBarEnd: '#0b5c32',
    brandPrimary: '#0a4d28',
    brandPrimaryLight: '#fcd116',
    footerBg: '#02140a',
    pageHeader: '#064020',
    surfaceTint: '#e6f2ea'
  },
  {
    id: 'easter_lilac',
    name: 'Easter / Good Friday',
    description: 'Soft spring accent · petals',
    effect: 'easter',
    defaultPopup: 'Wishing you a peaceful Easter from Tema General Hospital.',
    announcementBar: '#5b3d8a',
    announcementBarEnd: '#8b6bb5',
    brandPrimary: '#5b3d8a',
    brandPrimaryLight: '#8b6bb5',
    footerBg: '#2f1f47',
    pageHeader: '#5b3d8a',
    surfaceTint: '#f6f1fb'
  },
  {
    id: 'ramadan',
    name: 'Ramadan',
    description: 'Holy month · deep navy & cyan · fanous lanterns, crescent moon & stars',
    effect: 'ramadan',
    defaultPopup:
      'Ramadan Mubarak / Ramadan Kareem from Tema General Hospital. Wishing you a blessed month of peace and good health.',
    announcementBar: '#061428',
    announcementBarEnd: '#0a4d8c',
    brandPrimary: '#0a3a6b',
    brandPrimaryLight: '#22d3ee',
    footerBg: '#020617',
    pageHeader: '#0b1f3a',
    surfaceTint: '#e8f4fc'
  },
  {
    id: 'eid_fitr',
    name: 'Eid al-Fitr',
    description: 'End of Ramadan · green & gold',
    effect: 'eid',
    defaultPopup: 'Eid Mubarak! Tema General Hospital wishes you joy, peace, and good health.',
    announcementBar: '#0f4c3a',
    announcementBarEnd: '#d4af37',
    brandPrimary: '#0f4c3a',
    brandPrimaryLight: '#d4af37',
    footerBg: '#083328',
    pageHeader: '#0f4c3a',
    surfaceTint: '#f3f9f4'
  },
  {
    id: 'eid_adha',
    name: 'Eid al-Adha',
    description: 'Festival of Sacrifice · green & gold',
    effect: 'eid',
    defaultPopup:
      'Eid al-Adha Mubarak from Tema General Hospital. May your celebrations be filled with health and happiness.',
    announcementBar: '#134e4a',
    announcementBarEnd: '#b8860b',
    brandPrimary: '#134e4a',
    brandPrimaryLight: '#b8860b',
    footerBg: '#0a322f',
    pageHeader: '#134e4a',
    surfaceTint: '#f0f7f5'
  },
  {
    id: 'workers_day',
    name: 'May Day / Labour Day',
    description: '1 May · Ghana flag (Red, Gold, Green & Black star)',
    effect: 'ghana',
    defaultPopup:
      'Happy May Day / Labour Day! Tema General Hospital salutes all workers who care for our communities.',
    announcementBar: '#ce1126',
    announcementBarEnd: '#006b3f',
    brandPrimary: '#ce1126',
    brandPrimaryLight: '#fcd116',
    footerBg: '#111111',
    pageHeader: '#006b3f',
    surfaceTint: '#fff8e7'
  },
  {
    id: 'mothers_day',
    name: "Mother's Day",
    description: 'Second Sunday in May · Pink, Purple & soft pastels',
    effect: 'mothers',
    defaultPopup:
      "Happy Mother's Day from Tema General Hospital. Thank you to all mothers and caregivers.",
    announcementBar: '#c2185b',
    announcementBarEnd: '#9c27b0',
    brandPrimary: '#c2185b',
    brandPrimaryLight: '#e1bee7',
    footerBg: '#6a1b4d',
    pageHeader: '#9c27b0',
    surfaceTint: '#fce4ec'
  },
  {
    id: 'au_day',
    name: 'AU Day / Africa Day',
    description: '25 May · African Union colours',
    effect: 'africa',
    defaultPopup:
      'Happy Africa Day / AU Day from Tema General Hospital. Celebrating unity and health across the continent.',
    announcementBar: '#006633',
    announcementBarEnd: '#fcd116',
    brandPrimary: '#006633',
    brandPrimaryLight: '#fcd116',
    footerBg: '#003d1f',
    pageHeader: '#006633',
    surfaceTint: '#f0f7f2'
  },
  {
    id: 'fathers_day',
    name: "Father's Day",
    description: 'Third Sunday in June · Blue, Black, Grey & earth brown',
    effect: 'fathers',
    defaultPopup:
      "Happy Father's Day from Tema General Hospital. Thank you to all fathers and guardians.",
    announcementBar: '#1a237e',
    announcementBarEnd: '#6d4c41',
    brandPrimary: '#1a237e',
    brandPrimaryLight: '#78909c',
    footerBg: '#212121',
    pageHeader: '#37474f',
    surfaceTint: '#eceff1'
  },
  {
    id: 'republic_day',
    name: 'Republic Day',
    description: '1 July · Ghana flag (Red, Gold, Green & Black star)',
    effect: 'ghana',
    defaultPopup:
      'Happy Republic Day! Tema General Hospital commemorates Ghana becoming a republic in 1960.',
    announcementBar: '#ce1126',
    announcementBarEnd: '#006b3f',
    brandPrimary: '#ce1126',
    brandPrimaryLight: '#fcd116',
    footerBg: '#111111',
    pageHeader: '#006b3f',
    surfaceTint: '#fff8e7'
  },
  {
    id: 'founders_day',
    name: "Founders' Day",
    description: '4 August · Ghana flag (Red, Gold, Green & Black star)',
    effect: 'ghana',
    defaultPopup:
      "Happy Founders' Day from Tema General Hospital. Honouring the nation’s founding leaders.",
    announcementBar: '#ce1126',
    announcementBarEnd: '#006b3f',
    brandPrimary: '#ce1126',
    brandPrimaryLight: '#fcd116',
    footerBg: '#111111',
    pageHeader: '#006b3f',
    surfaceTint: '#fff8e7'
  },
  {
    id: 'nkrumah_memorial',
    name: 'Kwame Nkrumah Memorial Day',
    description: '21 September · Ghana flag (Red, Gold, Green & Black star)',
    effect: 'ghana',
    defaultPopup:
      'Remembering Kwame Nkrumah on his birthday. Tema General Hospital honours Ghana’s first president and his legacy of service.',
    announcementBar: '#ce1126',
    announcementBarEnd: '#006b3f',
    brandPrimary: '#ce1126',
    brandPrimaryLight: '#fcd116',
    footerBg: '#111111',
    pageHeader: '#006b3f',
    surfaceTint: '#fff8e7'
  },
  {
    id: 'farmers_day',
    name: "Farmers' Day",
    description: 'First Friday in December · Green & earth brown',
    effect: 'farmers',
    defaultPopup:
      "Happy Farmers' Day! Tema General Hospital thanks Ghana’s farmers and fishermen for nourishing our nation.",
    announcementBar: '#2e7d32',
    announcementBarEnd: '#8d6e63',
    brandPrimary: '#2e7d32',
    brandPrimaryLight: '#a1887f',
    footerBg: '#3e2723',
    pageHeader: '#558b2f',
    surfaceTint: '#efebe9'
  },
  {
    id: 'christmas_red',
    name: 'Christmas Red',
    description: '25 December · snow, Santa & fireworks',
    effect: 'christmas',
    defaultPopup:
      'Merry Christmas from Tema General Hospital! Wishing you warmth, good health, and joy.',
    announcementBar: '#7a0c16',
    announcementBarEnd: '#c41e3a',
    brandPrimary: '#8b0000',
    brandPrimaryLight: '#c41e3a',
    footerBg: '#4a0a10',
    pageHeader: '#8b0000',
    surfaceTint: '#fdf2f3'
  },
  {
    id: 'christmas_green',
    name: 'Christmas Green',
    description: 'Holiday evergreen · snow, Santa & fireworks',
    effect: 'christmas',
    defaultPopup:
      'Merry Christmas from Tema General Hospital! Wishing you warmth, good health, and joy.',
    announcementBar: '#0b3d2e',
    announcementBarEnd: '#1f7a4d',
    brandPrimary: '#0b3d2e',
    brandPrimaryLight: '#1f7a4d',
    footerBg: '#06261c',
    pageHeader: '#0b3d2e',
    surfaceTint: '#eef8f2'
  },
  {
    id: 'boxing_day',
    name: 'Boxing Day',
    description: '26 December · festive accents (no snow)',
    effect: 'boxing',
    defaultPopup: 'Happy Boxing Day from Tema General Hospital! Enjoy the season in good health.',
    announcementBar: '#5c0a12',
    announcementBarEnd: '#0b3d2e',
    brandPrimary: '#5c0a12',
    brandPrimaryLight: '#1f7a4d',
    footerBg: '#2a0810',
    pageHeader: '#5c0a12',
    surfaceTint: '#f8f4f0'
  },
  {
    id: 'custom',
    name: 'Custom theme',
    description: 'Create your own colours & animation',
    effect: 'none',
    defaultPopup: '',
    announcementBar: '#003d82',
    announcementBarEnd: '#0056b3',
    brandPrimary: '#003d82',
    brandPrimaryLight: '#0056b3',
    footerBg: '#2c3e50',
    pageHeader: '#003d82',
    surfaceTint: '#eef4fa'
  },
  {
    id: 'health_teal',
    name: 'Health Teal',
    description: 'Calm clinical teal (no holiday effects)',
    effect: 'none',
    defaultPopup: '',
    announcementBar: '#0f5c5c',
    announcementBarEnd: '#1a8f8f',
    brandPrimary: '#0f5c5c',
    brandPrimaryLight: '#1a8f8f',
    footerBg: '#0a3636',
    pageHeader: '#0f5c5c',
    surfaceTint: '#eaf7f7'
  },
  {
    id: 'night_navy',
    name: 'Night Navy',
    description: 'Deep evening navy (no holiday effects)',
    effect: 'none',
    defaultPopup: '',
    announcementBar: '#101828',
    announcementBarEnd: '#1d4ed8',
    brandPrimary: '#152238',
    brandPrimaryLight: '#1d4ed8',
    footerBg: '#0b1220',
    pageHeader: '#152238',
    surfaceTint: '#eef2ff'
  }
];

function normalizeHex(value, fallback = '#003d82') {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const r = raw[1];
    const g = raw[2];
    const b = raw[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback.toLowerCase();
}

function hexToRgb(hex) {
  const h = normalizeHex(hex).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
}

function luminance({ r, g, b }) {
  const toLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

export function contrastText(backgroundHex) {
  const lum = luminance(hexToRgb(backgroundHex));
  return lum > 0.45 ? '#0f172a' : '#ffffff';
}

export function mutedOnBackground(backgroundHex) {
  const on = contrastText(backgroundHex);
  return on === '#ffffff' ? 'rgba(255,255,255,0.72)' : 'rgba(15,23,42,0.65)';
}

export function getPresetById(id) {
  return THEME_PRESETS.find((p) => p.id === id) || null;
}

/** Effect type for the active theme (none | christmas | eid | …). */
export function getThemeEffect(themeId) {
  const preset = getPresetById(themeId);
  if (preset?.effect) return preset.effect;
  const id = String(themeId || '').toLowerCase();
  if (id.startsWith('christmas')) return 'christmas';
  if (id.includes('eid')) return 'eid';
  if (id.includes('ramadan')) return 'ramadan';
  if (id.includes('mother')) return 'mothers';
  if (id.includes('father')) return 'fathers';
  if (id.includes('valentine') || id.includes('chocolate')) return 'valentine';
  if (id.includes('constitution')) return 'constitution';
  if (id.includes('independence')) return 'independence';
  if (
    id.includes('republic') ||
    id.includes('founders') ||
    id.includes('nkrumah') ||
    id.includes('workers') ||
    id.includes('labour') ||
    id.includes('may_day')
  ) {
    return 'ghana';
  }
  if (id.includes('africa') || id.includes('au_')) return 'africa';
  if (id.includes('easter') || id.includes('good_friday')) return 'easter';
  return 'none';
}

/** National public holidays that show the Ghana flag (not small particle accents). */
export function usesGhanaFlag(effectOrThemeId) {
  const value = String(effectOrThemeId || '').toLowerCase();
  if (value === 'ghana') return true;
  return getThemeEffect(effectOrThemeId) === 'ghana';
}

/** Independence Day: forest-green theme + live waving flag. */
export function usesIndependenceWave(effectOrThemeId) {
  const value = String(effectOrThemeId || '').toLowerCase();
  if (value === 'independence') return true;
  return getThemeEffect(effectOrThemeId) === 'independence';
}

/** Constitution Day uses gold theme + coat of arms accents. */
export function usesCoatOfArms(effectOrThemeId) {
  const value = String(effectOrThemeId || '').toLowerCase();
  if (value === 'constitution') return true;
  return getThemeEffect(effectOrThemeId) === 'constitution';
}

/** Ramadan: navy/cyan festive lanterns, moon & glowing stars. */
export function usesRamadanDecor(effectOrThemeId) {
  const value = String(effectOrThemeId || '').toLowerCase();
  if (value === 'ramadan') return true;
  return getThemeEffect(effectOrThemeId) === 'ramadan';
}

export function isFestiveTheme(themeId) {
  return getThemeEffect(themeId) !== 'none';
}

/** @deprecated Use isFestiveTheme / getThemeEffect */
export function isChristmasTheme(themeId) {
  return getThemeEffect(themeId) === 'christmas';
}

export function getDefaultPopupForTheme(themeId) {
  const preset = getPresetById(themeId);
  return preset?.defaultPopup || '';
}

export function parsePopupEnabled(value, themeId) {
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return isFestiveTheme(themeId);
}

export function getDefaultThemeColors() {
  return { ...DEFAULT_COLORS };
}

export function resolveFestiveEffect(settings = {}) {
  const explicit = String(
    settings.festiveEffect ?? settings.festive_effect ?? ''
  )
    .trim()
    .toLowerCase();
  const allowed = new Set(FESTIVE_EFFECT_OPTIONS.map((item) => item.id));
  if (explicit && allowed.has(explicit)) return explicit;

  const themeId = String(settings.activeTheme ?? settings.active_theme ?? '');
  return getThemeEffect(themeId);
}

export function buildThemeFromSettings(settings = {}) {
  const activeTheme = String(
    settings.activeTheme ?? settings.active_theme ?? DEFAULT_THEME_ID
  ).trim() || DEFAULT_THEME_ID;
  const fromPreset = getPresetById(activeTheme);
  const base = fromPreset
    ? {
        ...DEFAULT_COLORS,
        ...fromPreset,
        activeTheme: fromPreset.id,
        festiveEffect: fromPreset.effect || 'none'
      }
    : { ...DEFAULT_COLORS, activeTheme };

  const popupMessageRaw = settings.popupMessage ?? settings.popup_message;
  const popupMessage =
    popupMessageRaw !== undefined && popupMessageRaw !== null
      ? String(popupMessageRaw).trim()
      : String(base.defaultPopup || getDefaultPopupForTheme(activeTheme) || '').trim();

  const next = {
    announcementBar: normalizeHex(
      settings.announcementBar ?? settings.announcement_bar_color,
      base.announcementBar
    ),
    announcementBarEnd: normalizeHex(
      settings.announcementBarEnd ?? settings.announcement_bar_color_end,
      base.announcementBarEnd
    ),
    brandPrimary: normalizeHex(
      settings.brandPrimary ?? settings.brand_primary,
      base.brandPrimary
    ),
    brandPrimaryLight: normalizeHex(
      settings.brandPrimaryLight ?? settings.brand_primary_light,
      base.brandPrimaryLight
    ),
    footerBg: normalizeHex(settings.footerBg ?? settings.footer_bg, base.footerBg),
    pageHeader: normalizeHex(settings.pageHeader ?? settings.page_header, base.pageHeader),
    surfaceTint: normalizeHex(settings.surfaceTint ?? settings.surface_tint, base.surfaceTint),
    activeTheme,
    popupMessage,
    popupEnabled: parsePopupEnabled(
      settings.popupEnabled ?? settings.popup_enabled,
      activeTheme
    ),
    festiveEffect: resolveFestiveEffect({
      ...settings,
      activeTheme,
      festiveEffect: settings.festiveEffect ?? settings.festive_effect ?? base.festiveEffect
    })
  };

  return {
    ...next,
    effect: next.festiveEffect,
    onAnnouncement: contrastText(next.announcementBar),
    onPrimary: contrastText(next.brandPrimary),
    onPrimaryLight: contrastText(next.brandPrimaryLight),
    onFooter: contrastText(next.footerBg),
    footerMuted: mutedOnBackground(next.footerBg),
    onPageHeader: contrastText(next.pageHeader),
    onSurface: contrastText(next.surfaceTint)
  };
}

export function applyThemeColors(colors = {}) {
  const next = buildThemeFromSettings(colors);
  const root = document.documentElement;
  const primaryRgb = hexToRgb(next.brandPrimary);
  const lightRgb = hexToRgb(next.brandPrimaryLight);
  const headerRgb = hexToRgb(next.pageHeader);
  const primaryRgbCss = `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`;
  const lightRgbCss = `${lightRgb.r}, ${lightRgb.g}, ${lightRgb.b}`;
  const headerRgbCss = `${headerRgb.r}, ${headerRgb.g}, ${headerRgb.b}`;

  root.setAttribute('data-tgh-theme', next.activeTheme || 'custom');
  root.setAttribute('data-tgh-effect', next.effect || 'none');
  root.setAttribute('data-tgh-popup-enabled', next.popupEnabled ? '1' : '0');
  root.setAttribute('data-tgh-popup-message', next.popupMessage || '');

  root.style.setProperty('--tgh-blue', next.brandPrimary);
  root.style.setProperty('--tgh-blue-light', next.brandPrimaryLight);
  root.style.setProperty('--tgh-blue-rgb', primaryRgbCss);
  root.style.setProperty('--tgh-blue-light-rgb', lightRgbCss);
  root.style.setProperty('--tgh-page-header-rgb', headerRgbCss);
  root.style.setProperty('--tgh-announcement-start', next.announcementBar);
  root.style.setProperty('--tgh-announcement-end', next.announcementBarEnd);
  root.style.setProperty('--tgh-on-announcement', next.onAnnouncement);
  root.style.setProperty('--tgh-on-primary', next.onPrimary);
  root.style.setProperty('--tgh-on-primary-light', next.onPrimaryLight);
  root.style.setProperty('--tgh-footer-bg', next.footerBg);
  root.style.setProperty('--tgh-on-footer', next.onFooter);
  root.style.setProperty('--tgh-footer-muted', next.footerMuted);
  root.style.setProperty('--tgh-page-header', next.pageHeader);
  root.style.setProperty('--tgh-on-page-header', next.onPageHeader);
  root.style.setProperty('--tgh-surface-tint', next.surfaceTint);
  root.style.setProperty('--tgh-on-surface', next.onSurface);

  /* Bootstrap + Swiper follow the active event theme */
  root.style.setProperty('--bs-primary', next.brandPrimary);
  root.style.setProperty('--bs-primary-rgb', primaryRgbCss);
  root.style.setProperty('--bs-link-color', next.brandPrimaryLight);
  root.style.setProperty('--bs-link-hover-color', next.brandPrimary);
  root.style.setProperty('--swiper-theme-color', next.brandPrimaryLight);

  return next;
}

function themeSignature(data) {
  const colors = data?.colors || {};
  const announcements = Array.isArray(data?.announcements) ? data.announcements : [];
  return JSON.stringify({
    activeTheme: colors.activeTheme,
    announcementBar: colors.announcementBar,
    announcementBarEnd: colors.announcementBarEnd,
    brandPrimary: colors.brandPrimary,
    brandPrimaryLight: colors.brandPrimaryLight,
    footerBg: colors.footerBg,
    pageHeader: colors.pageHeader,
    surfaceTint: colors.surfaceTint,
    popupMessage: colors.popupMessage,
    popupEnabled: colors.popupEnabled,
    festiveEffect: colors.festiveEffect || colors.effect,
    messages: announcements.map((item) =>
      typeof item === 'string' ? item : `${item.id || ''}:${item.message || ''}`
    )
  });
}

/** Notify this tab and other open tabs that the public theme/announcements changed. */
export function notifyThemeUpdated() {
  window.dispatchEvent(new Event('tgh-theme-updated'));
  try {
    const channel = new BroadcastChannel('tgh-site-theme');
    channel.postMessage({ type: 'theme-updated', at: Date.now() });
    channel.close();
  } catch (_error) {
    // BroadcastChannel not available — polling still covers other visitors
  }
}

/**
 * Keep public pages in sync without a full refresh.
 * Polls the API and also listens for admin updates in other tabs.
 */
export function startPublicThemeLiveSync({ intervalMs = 10000 } = {}) {
  let cancelled = false;
  let lastSignature = '';
  let channel;

  const refresh = async ({ forceEvent = false } = {}) => {
    if (cancelled) return null;
    const data = await loadPublicThemeAndAnnouncements();
    if (cancelled) return data;
    const signature = themeSignature(data);
    const changed = Boolean(lastSignature) && signature !== lastSignature;
    lastSignature = signature;
    if (forceEvent || changed) {
      window.dispatchEvent(new Event('tgh-theme-updated'));
    }
    return data;
  };

  refresh();

  const intervalId = window.setInterval(() => {
    refresh();
  }, intervalMs);

  const onVisibility = () => {
    if (document.visibilityState === 'visible') refresh({ forceEvent: false });
  };
  document.addEventListener('visibilitychange', onVisibility);

  try {
    channel = new BroadcastChannel('tgh-site-theme');
    channel.onmessage = () => {
      refresh({ forceEvent: true });
    };
  } catch (_error) {
    channel = null;
  }

  return () => {
    cancelled = true;
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibility);
    if (channel) channel.close();
  };
}

export async function loadPublicThemeAndAnnouncements() {
  try {
    const response = await fetch('/api/announcements/public', { cache: 'no-store' });
    if (!response.ok) {
      return {
        announcements: [],
        colors: applyThemeColors(DEFAULT_COLORS)
      };
    }

    const data = await response.json();
    const colors = applyThemeColors(data.colors || DEFAULT_COLORS);
    return {
      announcements: Array.isArray(data.announcements) ? data.announcements : [],
      colors
    };
  } catch (_error) {
    return {
      announcements: [],
      colors: applyThemeColors(DEFAULT_COLORS)
    };
  }
}
